'use client';

import { useState, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import { Slider } from '@/shared/ui/shadcn/slider';
import {
  Download,
  Eraser,
  Maximize,
  MoreVertical,
  Pencil,
  Redo,
  RotateCcw,
  Stamp,
  Trash2,
  Type,
  Undo,
  Upload,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useDrawingStore } from '@/features/drawing/model';
import { cn } from '@/shared/lib/cn';
import { DrawingColorPicker } from './DrawingColorPicker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { getModifierKey } from '@/shared/lib/hooks/usePlatform';

interface EditorToolbarTopProps {
  isMobile: boolean;
  onCancel: () => void;
  onSave: () => void;
  onImport: () => void;
  onExport: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

// Top bar: cancel · zoom · history · import/export · edit actions · save.
// Tool selection + brush size live in EditorToolbarBottom and sit at the bottom
// of the screen so we match the iOS / Android layout (canvas centered between
// the two bars, tool controls reachable by thumb on mobile).
export function EditorToolbarTop({
  isMobile,
  onCancel,
  onSave,
  onImport,
  onExport,
  onZoomIn,
  onZoomOut,
}: EditorToolbarTopProps) {
  const t = useTranslations('control.editor');

  const {
    canvas,
    history,
    textBlocks,
    undo,
    redo,
    resetCanvas,
    clearCanvas,
    resetZoom,
    renderTextBlocksToCanvas,
  } = useDrawingStore();

  const hasTextBlocks = textBlocks.blocks.length > 0;

  // Progressive inlining of secondary groups. Each breakpoint moves a group
  // from the ⋯ overflow menu into the bar so wide screens show everything
  // directly (matches user request — "show items as icons in the top bar").
  // When all three are inline the overflow trigger disappears entirely.
  const inlineZoom = useMediaQuery('(min-width: 1024px)');
  const inlineImportExport = useMediaQuery('(min-width: 1280px)');
  const inlineEditActions = useMediaQuery('(min-width: 1536px)');

  const overflowHasItems =
    !inlineZoom ||
    !inlineImportExport ||
    !inlineEditActions ||
    // Burn-text is conditional; on wide screens we already inline it in the
    // edit-actions group, so it never lands in overflow when inlineEditActions.
    (hasTextBlocks && !inlineEditActions);

  return (
    <div className="bg-neutral-900 border-b border-neutral-700 flex items-center justify-between p-3 relative">
      {/* Cancel */}
      <button
        onClick={onCancel}
        className="flex items-center justify-center transition-colors text-white hover:text-gray-300 p-2"
        title={t('cancel')}
      >
        <X className={cn(isMobile ? 'h-6 w-6' : 'h-5 w-5')} />
      </button>

      {/* Title (centered, desktop only) */}
      <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <h2 className="text-card-title text-white">{t('title')}</h2>
      </div>

      <div className="flex items-center gap-1">
        {/* Zoom group */}
        {inlineZoom && (
          <>
            <button
              onClick={onZoomIn}
              title={t('zoomIn')}
              className="text-white hover:text-gray-300 p-2 transition-colors"
            >
              <ZoomIn className="h-5 w-5" />
            </button>
            <button
              onClick={onZoomOut}
              title={t('zoomOut')}
              className="text-white hover:text-gray-300 p-2 transition-colors"
            >
              <ZoomOut className="h-5 w-5" />
            </button>
            <button
              onClick={resetZoom}
              title={t('resetZoom')}
              className="text-white hover:text-gray-300 p-2 transition-colors"
            >
              <Maximize className="h-5 w-5" />
            </button>
            <span className="h-5 w-px bg-neutral-700 mx-1" />
          </>
        )}

        {/* History group — always inline */}
        <button
          onClick={undo}
          disabled={!history.canUndo}
          title="Undo (Ctrl+Z)"
          className={cn(
            'text-white hover:text-gray-300 transition-colors disabled:opacity-30',
            isMobile ? 'p-2' : 'p-2'
          )}
        >
          <Undo className={cn(isMobile ? 'h-6 w-6' : 'h-5 w-5')} />
        </button>
        <button
          onClick={redo}
          disabled={!history.canRedo}
          title="Redo (Ctrl+Y)"
          className={cn(
            'text-white hover:text-gray-300 transition-colors disabled:opacity-30',
            isMobile ? 'p-2' : 'p-2'
          )}
        >
          <Redo className={cn(isMobile ? 'h-6 w-6' : 'h-5 w-5')} />
        </button>

        {/* Import / Export group */}
        {inlineImportExport && (
          <>
            <span className="h-5 w-px bg-neutral-700 mx-1" />
            <button
              onClick={onImport}
              title={t('loadImage')}
              className="text-white hover:text-gray-300 p-2 transition-colors"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={onExport}
              title={t('exportImage')}
              className="text-white hover:text-gray-300 p-2 transition-colors"
            >
              <Upload className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Edit-actions group — burn / reset / clear */}
        {inlineEditActions && (
          <>
            <span className="h-5 w-px bg-neutral-700 mx-1" />
            {hasTextBlocks && (
              <button
                onClick={renderTextBlocksToCanvas}
                title={t('burnTextToCanvas')}
                className="text-white hover:text-gray-300 p-2 transition-colors"
              >
                <Stamp className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={resetCanvas}
              title={t('resetToOriginal')}
              className="text-white hover:text-gray-300 p-2 transition-colors"
            >
              <RotateCcw className="h-5 w-5" />
            </button>
            <button
              onClick={clearCanvas}
              title={t('clearAll')}
              className="text-white hover:text-gray-300 p-2 transition-colors"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Overflow — collapsed groups + actions not yet inlined.
            modal={false} keeps Radix from setting pointer-events:none on body
            (Radix's default modal-overlay can swallow the very click that opens
            the menu when the trigger sits inside another fixed/portal layer). */}
        {overflowHasItems && (
          <>
            <span className="h-5 w-px bg-neutral-700 mx-1" />
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title={t('moreOptions')}
                  className="text-white hover:text-gray-300 p-2 transition-colors"
                >
                  <MoreVertical className={cn(isMobile ? 'h-6 w-6' : 'h-5 w-5')} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="z-[100002]">
                {!inlineZoom && (
                  <>
                    <DropdownMenuItem onClick={onZoomIn}>
                      <ZoomIn className="h-4 w-4" />
                      {t('zoomIn')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onZoomOut}>
                      <ZoomOut className="h-4 w-4" />
                      {t('zoomOut')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={resetZoom}>
                      <Maximize className="h-4 w-4" />
                      {t('resetZoom')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {!inlineImportExport && (
                  <>
                    <DropdownMenuItem onClick={onImport}>
                      <Download className="h-4 w-4" />
                      {t('loadImage')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onExport}>
                      <Upload className="h-4 w-4" />
                      {t('exportImage')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {!inlineEditActions && hasTextBlocks && (
                  <DropdownMenuItem onClick={renderTextBlocksToCanvas}>
                    <Stamp className="h-4 w-4" />
                    {t('burnTextToCanvas')}
                  </DropdownMenuItem>
                )}
                {!inlineEditActions && (
                  <>
                    <DropdownMenuItem onClick={resetCanvas}>
                      <RotateCcw className="h-4 w-4" />
                      {t('resetToOriginal')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={clearCanvas}>
                      <Trash2 className="h-4 w-4" />
                      {t('clearAll')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}

        <Button
          onClick={onSave}
          disabled={!canvas.hasChanges}
          className="bg-teal-600 hover:bg-teal-700 text-white ms-1"
        >
          {t('save')}
        </Button>
      </div>
    </div>
  );
}

interface EditorToolbarBottomProps {
  isMobile: boolean;
  canvasTransform: { zoom: number; pan: { x: number; y: number } };
  colorPickerOpen: boolean;
  onColorPickerOpenChange: (open: boolean) => void;
}

// Bottom bar: tool selection + brush size + color. Sits at the bottom of the
// editor like the iOS / Android counterparts so the canvas (in the middle) is
// flanked by both control bars.
export function EditorToolbarBottom({
  isMobile,
  canvasTransform,
  colorPickerOpen,
  onColorPickerOpenChange,
}: EditorToolbarBottomProps) {
  const t = useTranslations('control.editor');
  const [showSliderPreview, setShowSliderPreview] = useState(false);
  const [sliderPreviewPos, setSliderPreviewPos] = useState({ x: 0, y: 0 });
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const modifierKey = getModifierKey();

  const {
    tool,
    brushSettings,
    ui,
    setTool,
    setBrushSliderValue,
    setBrushColor,
  } = useDrawingStore();

  return (
    <>
      <div className="bg-neutral-900 border-t border-neutral-700">
        {/* Tool icons */}
        <div className="flex items-center justify-center p-3">
          <div
            className={cn('flex items-center', isMobile ? 'gap-4' : 'gap-3')}
          >
            <button
              onClick={() => setTool('pencil')}
              title={`${t('pencil')} (P)`}
              className={cn(
                'flex items-center justify-center transition-colors rounded-md',
                tool === 'pencil'
                  ? 'bg-white text-black'
                  : 'bg-transparent text-white hover:bg-gray-800',
                isMobile ? 'p-3' : 'p-2 gap-2'
              )}
            >
              <Pencil className={cn(isMobile ? 'h-8 w-8' : 'h-6 w-6')} />
              {!isMobile && (
                <span className="hidden md:inline text-form-input">
                  {t('pencil')}
                </span>
              )}
            </button>
            <button
              onClick={() => setTool('eraser')}
              title={`${t('eraser')} (E)`}
              className={cn(
                'flex items-center justify-center transition-colors rounded-md',
                tool === 'eraser'
                  ? 'bg-white text-black'
                  : 'bg-transparent text-white hover:bg-gray-800',
                isMobile ? 'p-3' : 'p-2 gap-2'
              )}
            >
              <Eraser className={cn(isMobile ? 'h-8 w-8' : 'h-6 w-6')} />
              {!isMobile && (
                <span className="hidden md:inline text-form-input">
                  {t('eraser')}
                </span>
              )}
            </button>
            <button
              onClick={() => setTool('text')}
              title={`${t('text')} (T)`}
              className={cn(
                'flex items-center justify-center transition-colors rounded-md',
                tool === 'text'
                  ? 'bg-white text-black'
                  : 'bg-transparent text-white hover:bg-gray-800',
                isMobile ? 'p-3' : 'p-2 gap-2'
              )}
            >
              <Type className={cn(isMobile ? 'h-8 w-8' : 'h-6 w-6')} />
              {!isMobile && (
                <span className="hidden md:inline text-form-input">
                  {t('text')}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Brush size + color picker.
            min-h pins the row to the brush-tool height so hiding the color
            swatch (eraser/text) doesn't collapse the row. */}
        <div
          className={cn(
            'flex items-center pb-3',
            isMobile ? 'px-4 gap-3 min-h-10' : 'px-3 justify-center gap-3 min-h-8'
          )}
        >
          {!isMobile && (
            <span className="text-form-label text-white whitespace-nowrap">
              {t('brushSize')}:
            </span>
          )}
          <div
            ref={sliderContainerRef}
            className={cn(
              'flex items-center gap-2 relative',
              isMobile && 'flex-1'
            )}
            onPointerMove={(e) => {
              if (!sliderContainerRef.current) return;
              const rect = sliderContainerRef.current.getBoundingClientRect();
              setSliderPreviewPos({ x: e.clientX - rect.left, y: -40 });
            }}
          >
            <Slider
              value={[ui.brushSliderValue]}
              onValueChange={([value]) => setBrushSliderValue(value ?? 0)}
              onPointerEnter={() => setShowSliderPreview(true)}
              onPointerLeave={() => setShowSliderPreview(false)}
              min={0}
              max={100}
              step={1}
              disabled={tool === 'text'}
              className={cn(isMobile ? 'flex-1' : 'w-32')}
            />
            {showSliderPreview &&
              (() => {
                const displaySize = brushSettings.size * canvasTransform.zoom;
                return (
                  <div
                    className="absolute pointer-events-none z-10"
                    style={{
                      left: sliderPreviewPos.x - displaySize / 2,
                      bottom: Math.abs(sliderPreviewPos.y) + 10,
                      transform: 'translateX(0)',
                    }}
                  >
                    <div
                      className="rounded-full opacity-60"
                      style={{
                        width: displaySize,
                        height: displaySize,
                        backgroundColor:
                          tool === 'eraser' ? '#ffffff' : brushSettings.color,
                        border: '2px solid rgba(156, 163, 175, 0.6)',
                        boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <div className="text-progress-value text-center mt-1 text-white bg-black/70 px-2 py-0.5 rounded whitespace-nowrap">
                      {brushSettings.size}px
                    </div>
                  </div>
                );
              })()}
          </div>

          {!isMobile && (
            <span className="text-list-item-subtitle text-white">
              {brushSettings.size}px
            </span>
          )}

          {/* Color Picker — hidden for eraser; color has no effect on erasing */}
          {tool === 'pencil' && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onColorPickerOpenChange(true);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onColorPickerOpenChange(true);
              }}
              className={cn(
                'flex-shrink-0 border-2 rounded-md transition-all hover:scale-105',
                isMobile ? 'h-10 w-10 p-1' : 'h-8 w-8 p-1'
              )}
              title={t('chooseColor')}
              style={{ borderColor: 'rgb(75, 85, 99)' }}
            >
              <div
                className="w-full h-full rounded-sm"
                style={{ backgroundColor: brushSettings.color }}
              />
            </button>
          )}

          {/* Keyboard hints (desktop only) */}
          {!isMobile && (
            <div className="hidden md:flex items-center gap-3 text-card-meta text-gray-400 ms-8">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-neutral-800 rounded border border-neutral-600 text-white">
                  X
                </kbd>
                <span>{t('switchTool')}</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-neutral-800 rounded border border-neutral-600 text-white">
                  Shift
                </kbd>
                <span>{t('straightLines')}</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-neutral-800 rounded border border-neutral-600 text-white">
                  Space
                </kbd>
                <span>{t('panCanvas')}</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-neutral-800 rounded border border-neutral-600 text-white">
                  Ctrl + Scroll
                </kbd>
                <span>{t('scrollForSize')}</span>
              </span>
              {tool === 'clone' && (
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-neutral-800 rounded border border-neutral-600 text-white">
                    {modifierKey}
                  </kbd>
                  <span>+ Click for clone source</span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Color Picker Dialog */}
      <DrawingColorPicker
        open={colorPickerOpen}
        onOpenChange={onColorPickerOpenChange}
        color={brushSettings.color}
        onChange={(color) => {
          setBrushColor(color);
          onColorPickerOpenChange(false);
        }}
        className="!z-[100001]"
        overlayClassName="!z-[100000]"
      />
    </>
  );
}
